import { useState } from 'react';
import type { WorkQueueCase } from '../../services/workQueue';
import { useCaseAssignment } from '../../hooks/useCaseAssignment';
import AssigneeSelector from '../conversations/AssigneeSelector';
import { TeamPill } from '@odo/shared-ui/components/TeamPill';
import { CARD_ASSIGNEE_TEAMS } from './constants';

interface TeamAssigneeCellProps {
    /** Already shaped for the slot this cell edits - see buildCells below. */
    workCase: WorkQueueCase;
    team: string;
    /** Team slot to target. Undefined takes the legacy untargeted path (moves the current
      slot and current_assigned_to), the only option for GP, which has no slot. */
    assignTeam?: string;
    assignee: string | null;
    /** Vertical rule between cells; omitted on the last one. */
    divided: boolean;
    onChange: (email: string | null) => void;
}

function TeamAssigneeCell({
    workCase,
    team,
    assignTeam,
    assignee,
    divided,
    onChange
}: TeamAssigneeCellProps) {
    const { roles, currentUserEmail, handleAssign, handleUnassign } = useCaseAssignment(
        workCase,
        'CompletionCard',
        assignTeam
    );

    const onAssign = async (email: string): Promise<void> => {
        if (await handleAssign(email)) onChange(email);
    };
    const onUnassign = async (): Promise<void> => {
        if (await handleUnassign()) onChange(null);
    };

    return (
        <div
            className={`flex-1 min-w-0 px-4 flex items-center gap-2.5 ${
                divided ? 'border-r border-gray-100' : ''
            }`}
        >
            <TeamPill team={team} size="xs" />
            <AssigneeSelector
                assignedTo={assignee}
                roles={roles}
                onAssign={onAssign}
                onUnassign={onUnassign}
                currentUserEmail={currentUserEmail}
                ariaLabel={`${team} assignee`}
                dropUp
            />
        </div>
    );
}

interface CompletionCardAssigneesProps {
    workCase: WorkQueueCase;
    initialTeamAssignees: Record<string, string> | undefined;
    /** Team the case moved to; gets its own cell when it isn't Clinical/Admin. */
    newTeam: string;
    initialAssignee: string | null;
}

// Post-transition assignees kept locally (the completion state is a snapshot), one column
// per team with each team pill inline with its assignee.
export function CompletionCardAssignees({
    workCase,
    initialTeamAssignees,
    newTeam,
    initialAssignee
}: CompletionCardAssigneesProps) {
    // Defaulted: a backend that predates teamAssignees omits the key entirely,
    // and this card sits under the app's only error boundary.
    const [teamAssignees, setTeamAssignees] = useState<Record<string, string>>(
        initialTeamAssignees ?? {}
    );
    // The current-slot assignee, tracked separately from the per-team map
    // because the untargeted path writes current_assigned_to, not a team row.
    const [assignee, setAssignee] = useState<string | null>(initialAssignee);

    const onTeamChange = (team: string, email: string | null): void => {
        setTeamAssignees((prev) => {
            const next = { ...prev };
            if (email === null) delete next[team];
            else next[team] = email;
            return next;
        });
    };

    // Clinical and Admin always show; a case now owned by another team gets a third cell on
    // the legacy path so that team can still be handed the case.
    const cells = [
        ...CARD_ASSIGNEE_TEAMS.map((team) => ({
            team,
            assignTeam: team as string | undefined,
            workCase: { ...workCase, teamAssignees },
            assignee: teamAssignees[team] ?? null,
            onChange: (email: string | null) => onTeamChange(team, email)
        })),
        ...(CARD_ASSIGNEE_TEAMS.includes(newTeam as (typeof CARD_ASSIGNEE_TEAMS)[number])
            ? []
            : [
                  {
                      team: newTeam,
                      assignTeam: undefined,
                      workCase: { ...workCase, team: newTeam, teamAssignees, assignee },
                      assignee,
                      onChange: setAssignee
                  }
              ])
    ];

    return (
        <div className="col-span-3 border-t border-gray-100 py-3.5">
            <div className="px-4 text-[10px] font-bold tracking-[0.08em] uppercase text-[#AAB0B9] mb-2.5">
                Assigned to
            </div>
            <div className="flex items-start">
                {cells.map((cell, i) => (
                    <TeamAssigneeCell
                        key={cell.team}
                        workCase={cell.workCase}
                        team={cell.team}
                        assignTeam={cell.assignTeam}
                        assignee={cell.assignee}
                        divided={i < cells.length - 1}
                        onChange={cell.onChange}
                    />
                ))}
            </div>
        </div>
    );
}
